// OVR flat-rate tier math. Pure module: no imports, no window/fetch at import
// time — extracted from ovr-shipping.js so the loyalty pipeline and Vitest can
// use it without ovr-shipping's import chain (ltl-shipping-item calls
// createLogger at module level, which crashes Vitest's node env at import).

// Default shipping rate tiers (fallback if theme settings not available)
export const DEFAULT_SHIPPING_TIERS = [
    { threshold: 49.99, rate: 9.95 },
    { threshold: 99.99, rate: 12.95 },
    { threshold: 199.99, rate: 16.95 },
    { threshold: 499.99, rate: 34.95 },
    { threshold: 749.99, rate: 44.95 },
    { threshold: Infinity, rate: 54.95 }
];

/**
 * Get shipping tiers from theme settings or use defaults
 * @returns {Array|Object} tier array or config object with tiers
 */
export function getShippingTiers() {
    // Check if theme settings are available via window.ovrShippingConfig
    // typeof guard added in the extraction: callable from Vitest's node env.
    if (typeof window !== 'undefined' && window.ovrShippingConfig && window.ovrShippingConfig.tiers) {
        return window.ovrShippingConfig;
    }

    // Fallback to default tiers
    return DEFAULT_SHIPPING_TIERS;
}

/**
 * Calculate shipping rate based on cart total
 * @param {number} total - Cart total amount
 * @returns {Object} Object containing rate (number or string) and remaining amount to next tier
 * - rate: Either a numeric shipping cost or a promotional message string (e.g., "FREE SHIPPING")
 * - remaining: Either a dollar amount to reach the next tier, or a message about additional items
 */
export function calculateShippingRate(total, config = getShippingTiers()) {
    // Handle both legacy array format and new config object format
    const tiers = Array.isArray(config) ? config : config.tiers;
    const maxTierCount = config.max_tier_count || tiers.length;
    const specialMessage = config.max_tier_special_message || null;

    // Find the appropriate tier for this cart total
    for (let i = 0; i < maxTierCount; i++) {
        const tier = tiers[i];

        if (total <= tier.threshold) {
            // Check if this is the last active tier and has a special promotional message
            const isLastTier = (i === maxTierCount - 1);
            const hasSpecialMessage = specialMessage && specialMessage.trim() !== '';

            if (isLastTier && hasSpecialMessage) {
                // Return custom promotional message instead of rate
                return {
                    rate: specialMessage,
                    remaining: "Additional qualifying items ship at no extra cost!"
                };
            }

            // Calculate remaining amount to reach this tier's threshold
            const remaining = tier.threshold === Infinity
                ? "Additional qualifying items ship at no extra cost!"
                : tier.threshold - total;

            return { rate: tier.rate, remaining };
        }
    }

    // Fallback: cart exceeds all tier thresholds
    const lastActiveTier = tiers[Math.min(maxTierCount - 1, tiers.length - 1)];
    const hasSpecialMessage = specialMessage && specialMessage.trim() !== '';

    return {
        rate: hasSpecialMessage ? specialMessage : lastActiveTier.rate,
        remaining: "Additional qualifying items ship at no extra cost!"
    };
}

/**
 * Sum a cart the way the OVR banner does. Verbatim extraction of the loop that
 * lived in getCartPageOvrPricing, kept here (the pure module) so the loyalty
 * pipeline and Vitest can use it without ovr-shipping's import chain.
 *
 * Deliberately does NOT prefer sale prices: the loyalty box's flat-rate
 * sentence must agree with the Shipping row OVR injects, and that row is
 * computed from this basis. See the 2026-07-29 consolidation spec.
 * @param {Array} cartItems - storefront physicalItems or v3 line_items
 * @returns {number} cart total on OVR's basis
 */
export function ovrCartTotal(cartItems) {
    if (!cartItems) return 0;
    let total = 0;
    for (const item of cartItems) {
        const price = item?.price?.value || item?.listPrice || item?.purchase_price || 0;
        total += price * (item.quantity || 1);
    }
    return total;
}
